import { Shift } from "@shiftly/shared/src/schema";
import { OT_SHIFT_CODES_KEYS } from "@shiftly/shared/src/shiftCodes";

interface ModelStats {
  totalPPV: number;
  totalNonPPV: number;
  averageEarningsPerHour: number;
  noPpvEarnedPerHour: number;
  averageEarningsPer8HourShift: number;
  totalLivestream: number;
  completedCount: number;
  upcomingCount: number;
  totalHoursWorked: number;
  percentageOfEarnings?: number;
}

interface UserStats {
  shiftsCount: number;
  totalPPV: number;
  totalNonPPV: number;
  hoursWorked: number;
  earnedPerHour: number;
  noPpvEarnedPerHour: number;
  averagePPVPer8HourShift: number;
  averageNonPPVPer8HourShift: number;
  averagePPVPer12HourShift: number;
  averageNonPPVPer12HourShift: number;
  workerType: 'weekday' | 'weekend';
  percentageOfEarnings?: number;
}

export const calculateModelStats = (shifts: Shift[]): ModelStats => {
  const completedShifts = shifts.filter(s => s.submitted);
  const upcomingShifts = shifts.filter(s => !s.submitted);
  const totalPPV = completedShifts.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0);
  const totalNonPPV = completedShifts.reduce((sum, shift) => sum + (shift.noppv_earned || 0), 0);
  const totalLivestream = completedShifts.reduce((sum, shift) => sum + (shift.livestream_earned || 0), 0);
  
  // Calculate total hours worked
  const totalHoursWorked = completedShifts.reduce((sum, shift) => {
    if (shift.shift_code === 'COVER')
      return sum;
    const hoursWorked = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
    return sum + hoursWorked;
  }, 0);
  
  return {
    totalPPV,
    totalNonPPV,
    averageEarningsPerHour: totalHoursWorked > 0 ? totalPPV / totalHoursWorked : 0,
    noPpvEarnedPerHour: totalHoursWorked > 0 ? totalNonPPV / totalHoursWorked : 0,
    averageEarningsPer8HourShift: totalHoursWorked > 0 ? (totalPPV / totalHoursWorked) * 8 : 0,
    totalLivestream,
    completedCount: completedShifts.length,
    upcomingCount: upcomingShifts.length,
    totalHoursWorked
  };
};

export const calculateUserStats = (shifts: Shift[]): Record<string, UserStats> => {
  // First pass: Calculate basic stats and determine worker type
  const userStats = shifts.reduce((userStats, shift) => {
    if (!shift.submitted || !shift.user_id) return userStats;
    
    if (!userStats[shift.user_id]) {
      userStats[shift.user_id] = {
        shiftsCount: 0,
        totalPPV: 0,
        totalNonPPV: 0,
        hoursWorked: 0,
        earnedPerHour: 0,
        noPpvEarnedPerHour: 0,
        averagePPVPer8HourShift: 0,
        averageNonPPVPer8HourShift: 0,
        averagePPVPer12HourShift: 0,
        averageNonPPVPer12HourShift: 0,
        workerType: 'weekday',
        // Additional fields for worker type calculation
        weekendShifts: 0,
        weekdayShifts: 0,
        nonOTShifts: 0
      };
    }
    
    const stats = userStats[shift.user_id];
    stats.shiftsCount++;
    stats.totalPPV += shift.ppv_earned || 0;
    stats.totalNonPPV += shift.noppv_earned || 0;
    
    const hoursWorked = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
    stats.hoursWorked += hoursWorked;
    
    // Only count non-OT shifts for worker type determination
    if (!OT_SHIFT_CODES_KEYS.includes(shift.shift_code)) {
      stats.nonOTShifts++;
      if (shift.start_date_time.isWeekend) {
        stats.weekendShifts++;
      } else {
        stats.weekdayShifts++;
      }
    }
    
    return userStats;
  }, {} as Record<string, UserStats & { weekendShifts: number; weekdayShifts: number; nonOTShifts: number }>);

  // Second pass: Calculate final stats and determine worker type
  Object.entries(userStats).forEach(([_, stats]) => {
    // Determine worker type based on non-OT shifts (75% threshold)
    if (stats.nonOTShifts > 0 && (stats.weekendShifts / stats.nonOTShifts) >= 0.75) {
      stats.workerType = 'weekend';
    }

    // Calculate hourly stats
    if (stats.hoursWorked > 0) {
      stats.earnedPerHour = stats.totalPPV / stats.hoursWorked;
      stats.noPpvEarnedPerHour = stats.totalNonPPV / stats.hoursWorked;
      if (stats.workerType === 'weekend') {
        stats.averagePPVPer8HourShift = 0;
        stats.averageNonPPVPer8HourShift = 0;
        stats.averagePPVPer12HourShift = (stats.totalPPV / stats.hoursWorked) * 12;
        stats.averageNonPPVPer12HourShift = (stats.totalNonPPV / stats.hoursWorked) * 12;
      } else {
        stats.averagePPVPer8HourShift = (stats.totalPPV / stats.hoursWorked) * 8;
        stats.averageNonPPVPer8HourShift = (stats.totalNonPPV / stats.hoursWorked) * 8;
      }
    }

    // Remove temporary fields used for calculation
    delete (stats as any).weekendShifts;
    delete (stats as any).weekdayShifts;
    delete (stats as any).nonOTShifts;
  });

  return userStats;
};

export const calculatePercentageOfEarnings = (userPPV: number, modelTotalPPV: number): number => {
  if (modelTotalPPV <= 0) return 0;
  return (userPPV / modelTotalPPV) * 100;
}; 